import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  EMPTY_DENIM_CATALOG,
  FALLBACK_DENIM_CATALOG,
  GenerativeCatalog,
  GenerativeDenimCatalog,
  GenerativeSeason,
  getGenerativeCatalog,
  getGenerativeDenimCatalog,
  getGenerativeSeasons,
  normalizeDenimCatalog,
} from "./generativeCatalog";

/** Seasons and report-theme catalog shared by Collection Build and Image Generator. */
export const useGenerativeCatalog = ({
  market,
  category,
  seasonId,
  genre,
  seasonLabelHint = "",
  /** When false, skip the report-theme catalog request (seasons still load). */
  catalogEnabled = true,
}: {
  market: string;
  category: string;
  seasonId: string;
  genre: string;
  seasonLabelHint?: string;
  catalogEnabled?: boolean;
}) => {
  const [catalog, setCatalog] = useState<GenerativeCatalog | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(false);
  const [seasons, setSeasons] = useState<GenerativeSeason[]>([]);
  const [seasonsLoading, setSeasonsLoading] = useState(false);
  const [seasonsError, setSeasonsError] = useState(false);

  const seasonLabel =
    seasons.find(item => item.id === seasonId)?.label || seasonLabelHint;
  const hasReportThemes = Boolean(catalog?.macros.length);
  const hasCapsules = Boolean(catalog?.capsules?.length);

  useEffect(() => {
    if (!market || !category) {
      setSeasons([]);
      setSeasonsError(false);
      setSeasonsLoading(false);
      return;
    }

    let cancelled = false;
    setSeasonsLoading(true);
    setSeasonsError(false);
    (async () => {
      try {
        const nextSeasons = await getGenerativeSeasons(market, category);
        if (cancelled) {
          return;
        }
        setSeasons(nextSeasons);
      } catch {
        if (!cancelled) {
          setSeasons([]);
          setSeasonsError(true);
          toast.error("Unable to load seasons.");
        }
      } finally {
        if (!cancelled) {
          setSeasonsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [market, category]);

  useEffect(() => {
    if (!catalogEnabled || !market || !category || !seasonLabel) {
      setCatalog(null);
      setCatalogError(false);
      setCatalogLoading(false);
      return;
    }

    let cancelled = false;
    setCatalogLoading(true);
    setCatalogError(false);
    (async () => {
      try {
        const nextCatalog = await getGenerativeCatalog({
          season: seasonLabel,
          seasonId,
          market,
          category,
          genre,
        });
        if (cancelled) {
          return;
        }
        setCatalog(nextCatalog);
      } catch {
        if (!cancelled) {
          setCatalog(null);
          setCatalogError(true);
          toast.error("Unable to load the catalog.");
        }
      } finally {
        if (!cancelled) {
          setCatalogLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [catalogEnabled, market, category, seasonId, seasonLabel, genre]);

  return {
    catalog,
    setCatalog,
    catalogLoading,
    catalogError,
    seasons,
    setSeasons,
    seasonsLoading,
    seasonsError,
    seasonLabel,
    hasReportThemes,
    hasCapsules,
  };
};

export const useGenerativeDenimCatalog = ({
  notifyOnError = false,
  allowFallback = true,
  enabled = true,
}: {
  notifyOnError?: boolean;
  allowFallback?: boolean;
  /** When false, skip the denim-catalog request (e.g. Collection Build until season). */
  enabled?: boolean;
} = {}) => {
  const [denimCatalog, setDenimCatalog] = useState<GenerativeDenimCatalog>(
    allowFallback ? FALLBACK_DENIM_CATALOG : EMPTY_DENIM_CATALOG
  );
  const [denimLoading, setDenimLoading] = useState(!allowFallback && enabled);
  const [denimError, setDenimError] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setDenimLoading(false);
      setDenimError(false);
      return;
    }

    let cancelled = false;
    setDenimLoading(true);
    setDenimError(false);
    getGenerativeDenimCatalog()
      .then(next => {
        if (!cancelled && next) {
          setDenimCatalog(normalizeDenimCatalog(next));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDenimError(true);
          if (!allowFallback) {
            setDenimCatalog(EMPTY_DENIM_CATALOG);
          }
          if (notifyOnError) {
            toast.error(
              allowFallback
                ? "Unable to load the denim catalog. Using built-in denim IDs."
                : "Unable to load the denim catalog."
            );
          }
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDenimLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [notifyOnError, allowFallback, enabled]);

  return useMemo(
    () => ({
      ...denimCatalog,
      denimLoading,
      denimError,
    }),
    [denimCatalog, denimLoading, denimError]
  );
};
